'use client';
import React, { useState } from 'react';
import { MapPin, Sparkles, X } from 'lucide-react';
import PrintRunningOrder from '@/components/PrintRunningOrder';

interface TimelineEvent {
  id: string;
  time: string;
  title: string;
  subtitle: string;
  description: string;
  location: string;
  locationNote?: string;
  tag?: string;
  tagColor?: string;
  highlight?: boolean;
  note?: string;
  howToGetThere?: string;
  foodNote?: string;
  regattaLink?: boolean;
  seeMapLink?: boolean;
  saunaInterest?: boolean;
}

interface DaySection {
  id: string;
  dayLabel: string;
  date: string;
  dateNote: string;
  events: TimelineEvent[];
}

const weekendData: DaySection[] = [
  {
    id: 'day-friday',
    dayLabel: 'Friday',
    date: '28th August',
    dateNote: 'The eve',
    events: [
      {
        id: 'event-fri-eve',
        time: '17:00',
        title: 'Evening Gathering',
        subtitle: 'Galions Quay, optional',
        description: 'For those who\'d like to join us, we\'ll be gathering at Galions Quay, a beautiful house near the castle, for a relaxed evening of refreshments, nibbles and chilled music. This is entirely optional, but we\'d love to see you there! Running from 17:00 to 19:30, bring a bottle.',
        location: 'Galions Quay',
        locationNote: 'A house near Dartmouth Castle',
        tag: 'Optional',
        tagColor: 'bg-pale-olive/30 text-olive',
        howToGetThere: 'Castle Rd, Dartmouth TQ6 0JN. Parking is very limited in this area, but it\'s a short walk from town.',
        regattaLink: false,
      },
      {
        id: 'event-fri-regatta',
        time: '19:00',
        title: 'Regatta Festivities',
        subtitle: 'Dartmouth Town',
        description: 'Head into town to join in the Regatta festivities, a great way to round off the evening and soak up the atmosphere ahead of the big day.',
        location: 'Dartmouth Town',
        locationNote: 'A short walk from Galions Quay',
        tag: 'Evening',
        tagColor: 'bg-faded-navy/10 text-faded-navy',
        regattaLink: true,
      },
    ],
  },
  {
    id: 'day-saturday',
    dayLabel: 'Saturday',
    date: '29th August',
    dateNote: 'The wedding day',
    events: [
      {
        id: 'event-travel',
        time: '11:00',
        title: 'Travel to Church',
        subtitle: 'Dartmouth to St Petrox Church',
        description: 'There are two options: take the 15-minute passenger ferry from Dartmouth quayside; or set off on foot along the shore road (allow 25–30 minutes from Town)',
        location: 'Dartmouth Quayside to St Petrox Church',
        locationNote: 'Ferry: approximately 15 min crossing, departs approx. every 20 mins from Dartmouth quayside. On foot: approximately 25–30 mins along the shore road',
        tag: 'Getting There',
        tagColor: 'bg-pale-olive/30 text-olive',
        howToGetThere: 'To get to the centre of Dartmouth on Saturday, we recommend using the park and ride out of town, parking in Dartmouth itself will be very limited due to the Regatta.',
        seeMapLink: true,
      },
      {
        id: 'event-ceremony',
        time: '12:00',
        title: 'Wedding Ceremony',
        subtitle: 'St Petrox Church',
        description: 'The ceremony will take place at St Petrox Church, one of England\'s most beautifully situated medieval churches, perched at the mouth of the River Dart within the Dartmouth Castle complex. Please arrive by 11:30.',
        location: 'St Petrox Church, Dartmouth Castle',
        locationNote: 'Castle Road, Dartmouth TQ6 9JH. Access via passenger ferry from the Embankment or on foot along the shore road',
        tag: 'Ceremony',
        tagColor: 'bg-faded-navy/10 text-faded-navy',
        highlight: true,
        howToGetThere: 'See the travel section above for directions to the church. Vehicle drop-off is possible by the church, but please be aware that parking spaces are very limited — if you need help with parking, please get in touch and we\'ll do our best to assist.',
        foodNote: 'Please note, for those with young children or a good appetite, it might be good to eat before the ceremony as the main food and refreshments won\'t be until later in the day.',
      },
      {
        id: 'event-sugary',
        time: 'Post-ceremony',
        title: 'Refreshments at Castle Cove',
        subtitle: 'Castle Cove',
        description: 'Immediately after the ceremony, we invite you to join us at Castle Cove: a secluded little beach directly below Dartmouth Castle with turquoise water and rugged cliffs. A perfect spot for photographs and a glass of something cold. It will be low tide, so it\'s a great opportunity to get down to the water and enjoy the rugged little cove.',
        location: 'Castle Cove',
        locationNote: '5–10 min walk from the church via a steep coastal path',
        tag: 'Post-ceremony',
        tagColor: 'bg-warm-sand/20 text-stone',
        howToGetThere: 'Castle Cove is a two-minute walk from the church, but please note there are steps down to the cove. For those who would prefer not to use the steps, there is seating available at the top.',
      },
      {
        id: 'event-reception',
        time: '15:00',
        title: 'The Reception',
        subtitle: 'Gullsway Boathouse',
        description: 'The reception begins at Gullsway Boathouse, a waterfront spot with direct access to the River Dart. Expect an afternoon and evening of food, music, and celebration with spectacular views across the river to Kingswear and out to sea through the rivermouth. Look out for the final races of the Dartmouth Regatta!',
        location: 'Gullsway Boathouse, Dartmouth',
        locationNote: 'Waterfront · gullsway.co.uk',
        tag: 'Reception',
        tagColor: 'bg-olive/10 text-olive',
        highlight: true,
        howToGetThere: 'The entrance to Gullsway Boathouse is from the road "South Town" — please note there are a lot of steps down to the venue. Alternatively, a boat pick-up can be arranged, but please be aware this is only possible after 15:30 due to the tides. If you\'d like to arrange a boat pick-up, please let us know in advance.',
      },
      {
        id: 'event-fireworks',
        time: '21:00',
        title: 'Dartmouth Regatta Fireworks',
        subtitle: 'Over the River Dart',
        description: 'As if the universe planned it, the famous Dartmouth Regatta fireworks light up the harbour at 21:00 on our wedding day. One of the finest fireworks displays in the South West, viewable from the waterfront and from the Boathouse itself.',
        location: 'Dartmouth Waterfront',
        locationNote: 'Visible from Gullsway Boathouse and the Embankment',
        tag: 'Regatta',
        tagColor: 'bg-warm-sand/30 text-stone',
        note: 'Dartmouth Regatta 2026, the fireworks fall on our wedding evening. A spectacular coincidence.',
        howToGetThere: 'The fireworks can be enjoyed from the boathouse itself, or you\'re welcome to walk into town to watch from the waterfront, it\'s about a 5-minute walk.',
      },
      {
        id: 'event-boathouse-party',
        time: '21:00',
        title: 'Party Continues at Gullsway Boathouse',
        subtitle: 'Carriages and Boats at 23:00',
        description: 'The celebration carries on at Gullsway Boathouse. Carriages and boats depart at 23:00.',
        location: 'Gullsway Boathouse',
        locationNote: 'Dartmouth',
        tag: 'Evening',
        tagColor: 'bg-warm-sand/30 text-stone',
      },
      {
        id: 'event-afterparty',
        time: '23:00',
        title: 'After-Party in Dartmouth Town',
        subtitle: '',
        description: 'The night continues with an after-party in Dartmouth Town.',
        location: 'Dartmouth Town',
        locationNote: '',
        tag: 'Evening',
        tagColor: 'bg-warm-sand/30 text-stone',
        howToGetThere: 'The after-party is a 5-minute walk into town.',
      },
    ],
  },
  {
    id: 'day-sunday',
    dayLabel: 'Sunday',
    date: '30th August',
    dateNote: 'The morning after',
    events: [
      {
        id: 'event-brunch',
        time: '10:00 onwards',
        title: 'Morning on the Beach',
        subtitle: 'Blackpool Sands',
        description: 'A gentle, restorative morning at Blackpool Sands, one of South Devon\'s most beautiful beaches with an almost Mediterranean feel. The Cafe on the beach front will be open for refreshments or bring a picnic. Enjoy a cold water dip and the famous beach sauna nestled right on the shoreline. Kayaks and paddleboards available to hire but its sensible to book ahead.',
        location: 'Blackpool Sands',
        locationNote: 'A379, Stoke Fleming, TQ6 0RG. 10 mins from Dartmouth',
        tag: 'Sunday Morning',
        tagColor: 'bg-pale-olive/30 text-olive',
        howToGetThere: 'There is plenty of parking in the car park right at the beach. If you need a lift, taxis can be arranged or shared lifts organised — just let us know. Blackpool Sands is about a 10-minute drive out of Dartmouth.',
        saunaInterest: true,
      },
      {
        id: 'event-walk',
        time: '15:00',
        title: 'A few options',
        subtitle: '',
        description: "We'll see what everyone feels like doing, perhaps a cliff walk, a visit to the tropical gardens, or longer on the beach if the weather is good.",
        location: 'Dartmouth',
        locationNote: 'Meeting point to be confirmed',
        tag: 'Sunday Afternoon',
        tagColor: 'bg-pale-olive/30 text-olive',
      },
    ],
  },
];

export default function WeekendTimeline() {
  const [saunaModalOpen, setSaunaModalOpen] = useState(false);
  const [saunaName, setSaunaName] = useState('');
  const [saunaCount, setSaunaCount] = useState('');
  const [saunaSubmitted, setSaunaSubmitted] = useState(false);

  const handleSaunaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaunaSubmitted(true);
  };

  const closeSaunaModal = () => {
    setSaunaModalOpen(false);
    setSaunaSubmitted(false);
    setSaunaName('');
    setSaunaCount('');
  };

  return (
    <>
    <section className="pt-4 pb-24 px-8 md:px-16 xl:px-24">
      <div className="max-w-screen-2xl mx-auto">
        <div className="space-y-24">
          {weekendData.map((day) => (
            <div key={day.id}>
              {/* Day header */}
              <div className="flex items-baseline gap-6 mb-14">
                <div>
                  <p className="section-label mb-1">{day.dateNote}</p>
                  <h2 className="font-serif text-display-md text-faded-navy">{day.dayLabel}</h2>
                  <p className="font-serif text-xl text-stone italic mt-1">{day.date}</p>
                </div>
                <div className="flex-1 h-px bg-border mt-6 hidden md:block" />
              </div>

              {/* Events */}
              <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-1 lg:grid-cols-1">
                {day.events.map((event, idx) => (
                  <div
                    key={event.id}
                    className={`relative flex flex-col md:flex-row gap-0 ${
                      idx < day.events.length - 1 ? 'mb-0' : ''
                    }`}
                  >
                    {/* Time column */}
                    <div className="md:w-48 shrink-0 mb-4 md:mb-0">
                      <div className="flex md:flex-col items-center md:items-end gap-4 md:gap-2 md:pt-1">
                        <span className="font-serif text-lg text-faded-navy">{event.time}</span>
                        {event.tag && (
                          <span className={`text-xs tracking-[0.15em] uppercase font-sans px-3 py-1 ${event.tagColor}`}>
                            {event.tag}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Connector */}
                    <div className="hidden md:flex flex-col items-center mx-8 md:mx-10">
                      <div className={`w-3 h-3 rounded-full mt-1.5 border-2 shrink-0 ${
                        event.highlight ? 'border-faded-navy bg-faded-navy' : 'border-warm-sand bg-buttermilk'
                      }`} />
                      {idx < day.events.length - 1 && (
                        <div className="flex-1 w-px bg-border mt-2" style={{ minHeight: '60px' }} />
                      )}
                    </div>

                    {/* Content card */}
                    <div className={`relative flex-1 border p-8 mb-8 md:mb-0 transition-all duration-300 hover:shadow-sm ${
                      event.highlight
                        ? 'border-faded-navy/20 bg-off-white' :'border-border bg-buttermilk hover:border-warm-sand/50'
                    }`}>
                      {event.saunaInterest && (
                        <img
                          src="/assets/images/WildComfort-Apr-24-08482-819x1024-1777587511926.jpg"
                          alt="Beach sauna at Blackpool Sands"
                          className="absolute top-3 right-3 w-12 h-16 object-cover rounded-sm opacity-90"
                        />
                      )}
                      <h3 className="font-serif text-2xl text-faded-navy mb-1">{event.title}</h3>
                      <p className="font-sans text-sm text-warm-sand tracking-[0.05em] mb-4">{event.subtitle}</p>
                      <p className="font-sans text-sm text-stone leading-relaxed mb-5">{event.description}</p>

                      {event.saunaInterest && (
                        <div className="mb-5">
                          <button
                            onClick={() => setSaunaModalOpen(true)}
                            className="font-sans text-sm text-faded-navy border border-faded-navy/40 bg-faded-navy/5 hover:bg-faded-navy/10 px-5 py-2.5 transition-colors duration-200 tracking-[0.03em]"
                          >
                            We&apos;re interested in the Beach Sauna
                          </button>
                        </div>
                      )}

                      {event.regattaLink && (
                        <div className="mb-5">
                          <p className="font-sans text-sm text-stone leading-relaxed mb-2">
                            Please take a look at the Dartmouth Regatta page for a range of activities going on throughout Friday
                          </p>
                          <a
                            href="https://www.dartmouthregatta.co.uk"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-sans text-sm text-faded-navy underline underline-offset-2 hover:text-warm-sand transition-colors duration-200"
                          >
                            www.dartmouthregatta.co.uk
                          </a>
                        </div>
                      )}

                      {/* Location */}
                      <div className="flex items-start gap-2 mb-3">
                        <MapPin size={13} className="text-stone/60 mt-0.5 shrink-0" strokeWidth={1.5} />
                        <div>
                          <p className="font-sans text-xs text-faded-navy">{event.location}</p>
                          {event.locationNote && (
                            <p className="font-sans text-xs text-stone/60 mt-0.5">{event.locationNote}</p>
                          )}
                        </div>
                      </div>

                      {/* How to get there */}
                      {event.howToGetThere && (
                        <div className="mt-3 flex items-start gap-2 bg-pale-olive/10 border border-pale-olive/30 px-4 py-3">
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-olive mt-0.5 shrink-0"><path d="M3 12h18M3 12l4-4M3 12l4 4"/></svg>
                          <div>
                            <p className="font-sans text-xs text-olive uppercase tracking-[0.1em] mb-1">How to get there</p>
                            <p className="font-sans text-xs text-stone leading-relaxed">{event.howToGetThere}</p>
                            {event.seeMapLink && (
                              <a
                                href="#map"
                                className="inline-flex items-center gap-1.5 mt-3 font-sans text-xs text-faded-navy border border-faded-navy/30 bg-faded-navy/5 hover:bg-faded-navy/10 px-3 py-1.5 transition-colors duration-200"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>
                                See Map
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Food note */}
                      {event.foodNote && (
                        <div className="mt-3 flex items-start gap-2 bg-warm-sand/10 border border-warm-sand/30 px-4 py-3">
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-warm-sand mt-0.5 shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          <p className="font-sans text-xs text-stone leading-relaxed">{event.foodNote}</p>
                        </div>
                      )}

                      {/* Special note */}
                      {event.note && (
                        <div className="mt-5 flex items-start gap-3 bg-warm-sand/10 border border-warm-sand/20 p-4">
                          <Sparkles size={13} className="text-warm-sand mt-0.5 shrink-0" strokeWidth={1.5} />
                          <p className="font-sans text-xs text-stone leading-relaxed">{event.note}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Fireworks image */}
        <div className="mt-16 flex justify-center">
          <div className="relative w-full max-w-2xl">
            <img
              src="/assets/images/IMG_4002-1777046902376.jpeg"
              alt="Fireworks display at the wedding celebration"
              className="w-full rounded-sm object-cover"
            />
            <p className="absolute bottom-2 right-3 z-20 text-white/70" style={{ fontSize: '9px', letterSpacing: '0.02em' }}>
              Fireworks over Dartmouth Harbour, Aug 25
            </p>
          </div>
        </div>

        {/* Print running order */}
        <div className="mt-12 flex justify-center">
          <PrintRunningOrder />
        </div>

        {/* Dress code note */}
        <div className="mt-24 border-t border-border pt-16 grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Arrival */}
          <div>
            <p className="section-label mb-4">Arrival</p>
            <p className="font-sans text-sm text-stone leading-relaxed">Please aim to be seated at St Petrox Church by 11:45. The passenger ferry from Dartmouth quayside takes approximately 15 minutes and departs roughly every 20 minutes, board by 11:30 to arrive in good time. Alternatively, the church is reachable on foot along the shore road in around 25–30 minutes. There is limited parking near the castle.</p>
          </div>
          {/* Children & Infants */}
          <div>
            <p className="section-label mb-4">Children &amp; Infants</p>
            <p className="font-sans text-sm text-stone leading-relaxed">Children are warmly welcome at the ceremony and reception. Please let us know on your RSVP so we can make sure there is space and catering for little ones.</p>
          </div>
        </div>

      </div>
    </section>

    {/* Beach Sauna Interest Modal */}
    {saunaModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-faded-navy/40 backdrop-blur-sm">
        <div className="relative bg-buttermilk border border-border w-full max-w-md p-8 shadow-lg">
          <button
            onClick={closeSaunaModal}
            className="absolute top-4 right-4 text-stone/50 hover:text-stone transition-colors duration-200"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>

          {saunaSubmitted ? (
            <div className="text-center py-4">
              <p className="font-serif text-2xl text-faded-navy mb-3">Wonderful!</p>
              <p className="font-sans text-sm text-stone leading-relaxed">
                Thank you, {saunaName}! We&apos;ve noted your interest in the beach sauna. We&apos;ll be in touch with more details closer to the day.
              </p>
              <button
                onClick={closeSaunaModal}
                className="mt-6 font-sans text-sm text-faded-navy border border-faded-navy/40 bg-faded-navy/5 hover:bg-faded-navy/10 px-5 py-2.5 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="font-serif text-2xl text-faded-navy mb-1">Beach Sauna Interest</p>
              <p className="font-sans text-xs text-stone/60 tracking-[0.05em] mb-6">Blackpool Sands · Sunday 30th August</p>
              <p className="font-sans text-sm text-stone leading-relaxed mb-6">
                Let us know you&apos;re interested in a beach sauna session and how many in your group — we&apos;ll use this to help coordinate bookings.
              </p>
              <form onSubmit={handleSaunaSubmit} className="space-y-4">
                <div>
                  <label className="block font-sans text-xs text-faded-navy uppercase tracking-[0.1em] mb-2">
                    Your name
                  </label>
                  <input
                    type="text"
                    value={saunaName}
                    onChange={(e) => setSaunaName(e.target.value)}
                    required
                    placeholder="e.g. Sarah & Tom"
                    className="w-full border border-border bg-off-white px-4 py-2.5 font-sans text-sm text-stone placeholder:text-stone/40 focus:outline-none focus:border-faded-navy/40 transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="block font-sans text-xs text-faded-navy uppercase tracking-[0.1em] mb-2">
                    How many people?
                  </label>
                  <input
                    type="number"
                    value={saunaCount}
                    onChange={(e) => setSaunaCount(e.target.value)}
                    required
                    min="1"
                    placeholder="e.g. 2"
                    className="w-full border border-border bg-off-white px-4 py-2.5 font-sans text-sm text-stone placeholder:text-stone/40 focus:outline-none focus:border-faded-navy/40 transition-colors duration-200"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full font-sans text-sm text-off-white bg-faded-navy hover:bg-faded-navy/90 px-5 py-3 transition-colors duration-200 tracking-[0.05em] mt-2"
                >
                  Express Interest
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}